import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { adminApi, brl, errMsg } from "@/lib/api";
import { ALL_MODULES, LIMITS, SETTING_KEYS } from "./configSchema";
import { ModulesPanel, LimitsPanel, SettingsPanel } from "./ConfigPanels";
import { toast } from "sonner";

const strMap = (o = {}) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, String(v)]));

function toState(d) {
  return { usePlan: d.modules_override == null, modules: d.modules_override || [], prices: strMap(d.module_prices),
    limits: strMap(d.limits_override), settings: Object.fromEntries(SETTING_KEYS.map((k) => [k, d.settings[k]])) };
}

function toBody(c) {
  return { use_plan_modules: c.usePlan, modules: c.usePlan ? null : c.modules,
    module_prices: Object.fromEntries(Object.entries(c.prices).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])),
    limits: Object.fromEntries(LIMITS.map(([k]) => [k, c.limits[k] === "" || c.limits[k] == null ? null : Number(c.limits[k])])),
    settings: c.settings };
}

export function TenantConfigDialog({ tenantId, onOpenChange, onSaved }) {
  const [d, setD] = useState(null);
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setD(null); setC(null);
    if (tenantId) adminApi.get(`/tenants/${tenantId}`).then(({ data }) => { setD(data); setC(toState(data)); }).catch((e) => toast.error(errMsg(e)));
  }, [tenantId]);
  const set = (p) => setC((x) => ({ ...x, ...p }));
  const plan = d?.plan_info;
  const trial = !plan && d?.trial_modules_snapshot != null;
  const planMods = plan?.modules || (trial ? d.trial_modules_snapshot : ALL_MODULES);
  const active = c ? (c.usePlan ? planMods : c.modules) : [];
  const extra = active.reduce((s, m) => s + (Number(c?.prices[m]) || 0), 0);
  const base = Number(plan?.price || 0);
  const save = async () => {
    setBusy(true);
    try { await adminApi.put(`/tenants/${tenantId}/config`, toBody(c)); toast.success("Módulos e valores salvos"); onSaved(); onOpenChange(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!tenantId} onOpenChange={(v) => !v && onOpenChange()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tenant-config-dialog">
        <DialogHeader><DialogTitle>Módulos e valores · {d?.nome_fantasia || "Carregando..."}</DialogTitle></DialogHeader>
        {c && (
          <>
            <Tabs defaultValue="modulos">
              <TabsList className="grid h-auto w-full grid-cols-3">
                <TabsTrigger value="modulos" data-testid="cfg-tab-modules">Módulos e preços</TabsTrigger>
                <TabsTrigger value="limites" data-testid="cfg-tab-limits">Limites</TabsTrigger>
                <TabsTrigger value="config" data-testid="cfg-tab-settings">Configurações</TabsTrigger>
              </TabsList>
              <TabsContent value="modulos" className="mt-4"><ModulesPanel c={c} set={set} planMods={planMods} planName={plan?.name} trial={trial} /></TabsContent>
              <TabsContent value="limites" className="mt-4"><LimitsPanel c={c} set={set} plan={plan} /></TabsContent>
              <TabsContent value="config" className="mt-4"><SettingsPanel c={c} set={set} active={active} /></TabsContent>
            </Tabs>
            <div className="flex flex-wrap items-center gap-3 border-t pt-4">
              <p className="flex-1 text-sm" data-testid="cfg-monthly">
                Mensalidade: {plan ? `plano ${brl(base)}` : "sem plano ativo"} + módulos {brl(extra)} = <b>{brl(base + extra)}</b>/mês
              </p>
              <Button onClick={save} disabled={busy} data-testid="cfg-save-btn">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Salvar</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
